import React, { useState, useRef } from 'react';
import { Input, InputTypes } from '@components/atoms/Input';
import styles from './OTPInput.module.css';

const OTP_LENGTH = 6;

interface OTPInputProps {
    value: string;
    onChange: (otp: string) => void;
}

export const OTPInput: React.FC<OTPInputProps> = ({ value, onChange }) => {
    const [otp, setOtp] = useState<string[]>(Array(OTP_LENGTH).fill(''));
    const containerRef = useRef<HTMLDivElement>(null);

    const focusInput = (index: number) => {
        const inputs = containerRef.current?.querySelectorAll('input');
        if (inputs && inputs[index]) (inputs[index] as HTMLInputElement).focus();
    };

    const handleChange = (val: string, idx: number) => {
        const digit = val.replace(/\D/g, '');
        if (!digit) return;

        const updated = [...otp];
        updated[idx] = digit[0];
        setOtp(updated);

        const nextIndex = idx + 1;
        if (nextIndex < OTP_LENGTH) focusInput(nextIndex);

        onChange(updated.join(''));
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        const inputs = containerRef.current?.querySelectorAll('input');
        if (!inputs) return;

        const index = Array.from(inputs).findIndex((input) => input === document.activeElement);
        if (index === -1) return;

        if (e.key === 'Backspace') {
            e.preventDefault();
            const updated = [...otp];
            if (updated[index]) {
                updated[index] = '';
                setOtp(updated);
                onChange(updated.join(''));
            } else if (index > 0) {
                updated[index - 1] = '';
                setOtp(updated);
                onChange(updated.join(''));
                focusInput(index - 1);
            }
        }
    };

    const handlePaste = (e: React.ClipboardEvent) => {
        e.preventDefault();
        const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, OTP_LENGTH).split('');
        const updated = [...otp];
        pasted.forEach((d, i) => (updated[i] = d));
        setOtp(updated);
        onChange(updated.join(''));
        if (pasted.length < OTP_LENGTH) focusInput(pasted.length);
    };

    return (
        <div
            ref={containerRef}
            onKeyDown={handleKeyDown}
            onPaste={handlePaste}
            className="d-flex justify-content-center"
            style={{ gap: '8px' }}>
            {otp.map((digit, idx) => (
                <Input
                    key={idx}
                    type={InputTypes.text}
                    value={digit}
                    maxLength={1}
                    onChange={(val) => handleChange(val, idx)}
                    classes={`${styles.inputColor} text-center`}
                />
            ))}
        </div>
    );
};
